import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  ArrowLeft,
  Link as LinkIcon,
  EnvelopeSimple,
  Phone,
  Spinner,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const FirecrawlPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [name, setName] = useState("");
  const [urlsText, setUrlsText] = useState("");
  const [mode, setMode] = useState("single"); // "single" | "crawl"
  const [crawlLimit, setCrawlLimit] = useState(50);
  const [enrichEmails, setEnrichEmails] = useState(true);
  const [enrichPhones, setEnrichPhones] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const urls = useMemo(
    () =>
      urlsText
        .split(/[\r\n,]+/)
        .map((u) => u.trim())
        .filter(Boolean),
    [urlsText],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Give your job a name.");
      return;
    }
    if (urls.length === 0) {
      toast.error("Paste at least one URL.");
      return;
    }
    if (urls.length > 25) {
      toast.error("Max 25 starting URLs per job. Split into multiple jobs.");
      return;
    }
    if (!enrichEmails && !enrichPhones) {
      toast.error("Pick at least one enrichment mode: Email, Phone, or both.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/jobs/firecrawl`,
        {
          name: name.trim(),
          urls,
          mode,
          crawl_limit: Number(crawlLimit) || 50,
          enrich_emails: enrichEmails,
          enrich_phones: enrichPhones,
        },
        { headers: getAuthHeaders(), timeout: 60000 },
      );
      toast.success(`Fire Crawl started — scraping ${urls.length} URL${urls.length > 1 ? "s" : ""}. Jump to the job page to watch progress.`);
      navigate(`/jobs/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start Fire Crawl job");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="firecrawl-page">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          className="text-slate-600 hover:text-slate-900"
          data-testid="firecrawl-back-btn"
        >
          <ArrowLeft size={18} className="mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Fire Crawl</h1>
          <p className="text-sm text-slate-500">
            Turn any public web page into a lead list. Paste one URL per line — we scrape, extract,
            clean, dedupe, and (optionally) enrich + verify.
          </p>
        </div>
      </div>

      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg text-slate-900">Fire Crawl a URL</CardTitle>
          <CardDescription className="text-slate-500">
            Works with agent directories, company Team pages, event attendee lists, member
            portals — anywhere structured contact info sits on the page.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Job name */}
            <div className="space-y-2">
              <Label className="text-slate-700">Job Name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="KW Dallas agents — Fire Crawl"
                className="bg-white border-slate-200"
                data-testid="firecrawl-name"
              />
            </div>

            {/* URLs */}
            <div className="space-y-2">
              <Label className="text-slate-700 flex items-center gap-2">
                <LinkIcon size={16} /> URLs *
              </Label>
              <textarea
                value={urlsText}
                onChange={(e) => setUrlsText(e.target.value)}
                rows={5}
                placeholder={"One URL per line, e.g.\nhttps://www.remax.com/real-estate-agents/dallas-tx\nhttps://kw.com/agents/office-xyz"}
                className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-[#006994] font-mono"
                data-testid="firecrawl-urls"
              />
              <p className="text-xs text-slate-500">
                {urls.length} URL{urls.length !== 1 ? "s" : ""} · max 25 per job.
              </p>
            </div>

            {/* Mode */}
            <div className="space-y-2">
              <Label className="text-slate-700">Scrape mode</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label
                  className={`border rounded-md p-3 cursor-pointer transition-colors ${
                    mode === "single"
                      ? "border-[#006994] bg-[#006994]/5"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid="firecrawl-mode-single"
                >
                  <input
                    type="radio"
                    name="mode"
                    checked={mode === "single"}
                    onChange={() => setMode("single")}
                    className="mr-2 accent-[#006994]"
                  />
                  <span className="font-medium text-slate-900">Single page</span>
                  <p className="text-xs text-slate-500 mt-1">
                    Scrape only the pasted URLs. Fastest, cheapest, most predictable.
                  </p>
                </label>
                <label
                  className={`border rounded-md p-3 cursor-pointer transition-colors ${
                    mode === "crawl"
                      ? "border-[#006994] bg-[#006994]/5"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid="firecrawl-mode-crawl"
                >
                  <input
                    type="radio"
                    name="mode"
                    checked={mode === "crawl"}
                    onChange={() => setMode("crawl")}
                    className="mr-2 accent-[#006994]"
                  />
                  <span className="font-medium text-slate-900">Follow pagination / subpages</span>
                  <p className="text-xs text-slate-500 mt-1">
                    Follow internal links up to <em>N</em> pages per URL. Best for paginated
                    directories.
                  </p>
                </label>
              </div>
              {mode === "crawl" && (
                <div className="mt-3">
                  <Label className="text-slate-700 text-xs">Pages per URL (1-500)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={500}
                    value={crawlLimit}
                    onChange={(e) => setCrawlLimit(e.target.value)}
                    className="bg-white border-slate-200 mt-1 max-w-[160px]"
                    data-testid="firecrawl-crawl-limit"
                  />
                </div>
              )}
            </div>

            {/* Enrichment toggles — mirror the New Job form pattern */}
            <div className="space-y-3">
              <Label className="text-slate-700">Enrichment</Label>

              <div className="p-4 rounded-lg bg-[#0EA5E9]/5 border border-[#0EA5E9]/20">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enrichEmails}
                    onChange={(e) => setEnrichEmails(e.target.checked)}
                    className="w-5 h-5 rounded border-[#0EA5E9]/50 bg-slate-100 text-[#0EA5E9] focus:ring-[#0EA5E9] focus:ring-offset-0 accent-[#0EA5E9]"
                    data-testid="firecrawl-enrich-emails"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <EnvelopeSimple size={18} className="text-[#0EA5E9]" />
                      <span className="text-slate-900 font-medium">Enable Email Enrichment</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Verify every extracted email through MillionVerifier so only valid inboxes flow through to Instantly.
                    </p>
                  </div>
                </label>
              </div>

              <div className="p-4 rounded-lg bg-[#006994]/5 border border-[#006994]/20">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enrichPhones}
                    onChange={(e) => setEnrichPhones(e.target.checked)}
                    className="w-5 h-5 rounded border-[#006994]/50 bg-slate-100 text-[#006994] focus:ring-[#006994] focus:ring-offset-0 accent-[#006994]"
                    data-testid="firecrawl-enrich-phones"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <Phone size={18} className="text-[#006994]" />
                      <span className="text-slate-900 font-medium">Enable Phone Enrichment</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Validate &amp; normalise phone numbers (E.164) — for cold-call / voice drop / SMS campaigns (GHL). No extra API cost.
                    </p>
                  </div>
                </label>
              </div>

              <p className="text-xs text-slate-500 -mt-1 ml-1">
                Pick at least one. Tick both to keep leads with either email or phone.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="border-slate-200 text-slate-700"
                data-testid="firecrawl-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#006994] hover:bg-[#005A80] text-white font-semibold disabled:opacity-50"
                data-testid="firecrawl-submit"
              >
                {submitting ? (
                  <>
                    <Spinner size={16} className="mr-2 animate-spin" /> Queuing…
                  </>
                ) : (
                  "Start Fire Crawl"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default FirecrawlPage;
